import { TotalResults } from '@shared/components/ui/TotalResults';
import { EMPTY_VIEW_ICONS, EmptyView } from '@shared/components/ui/EmptyView';
import { usePermissions } from '@shared/hooks/usePermissions';
import { useEffect, useMemo, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import { useAgGridColumns } from '../../hooks/useAgGridColumns';
import { useOrdersCountByStatus } from '../../hooks/useOrdersCountByStatus';
import { useOrdersMain } from '../../hooks/useOrdersMain';
import { CardList, OrdersAgGridTable } from '../display';
import { ErrorState, LoadingState } from '../feedback';
import { CreateOrderButton, FiltersSection } from '../interaction';
import { StatusToolbar } from '../interaction/StatusToolbar';

export const Main = () => {
  const { t } = useTranslation('ordersModule');

  // Estado para controlar que el spinner de carga inicial se muestre solo una vez
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // Hook que maneja toda la lógica de negocio
  const {
    // Estados
    selectedOrders,
    selectedColumns,
    sorting,

    // Datos
    orders,
    isLoading,
    isFetching,
    error,
    total,

    // Contadores
    actionCounters,

    // Props para componentes
    ordersViewProps,

    // Handlers
    setStatusId,
    handleFilterChange,
    handleSearch,
    handleColumnsChange,
    handleCancelSelected,
    handlePrepareSelected,
    handleMarkForSendSelected,
    handleUncancelSelected,
    handleClearSelection,
    refetch,

    // Props para infinite scroll
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,

    // Handlers de selección
    handleSelectionChanged,

    // Estado de filtros
    hasActiveFilters,
    filterConditions,
    search,
    statusId,
    searchTerm,
    countFilters,
  } = useOrdersMain();

  // 🎯 Efecto para desactivar el spinner de carga inicial después del primer fetch
  useEffect(() => {
    // Si isLoading pasa de true a false, significa que la carga inicial terminó.
    if (!isLoading && isInitialLoad) {
      setIsInitialLoad(false);
    }
  }, [isLoading, isInitialLoad]);

  const [activeTab, setActiveTab] = useState('all');

  // 🎯 Verificar si el usuario es 3PL (solo ellos ven la columna de Cliente/Team)
  const { getTeamType } = usePermissions();
  const hasTeamData = getTeamType() === 'tenant_3pl';

  // 🎯 Hook para obtener contadores de pedidos por status
  const { counts: statusCounts, isLoading: isLoadingCounts } = useOrdersCountByStatus({
    search: searchTerm,
    searchFilter: countFilters.searchFilter,
    has_order_errors: countFilters.has_order_errors,
    general_error_ids: countFilters.general_error_ids,
  });

  // 🎯 Obtener columnas para el ColumnSelector usando el hook de AG Grid
  const { tanStackColumns } = useAgGridColumns({ hasTeamData, selectedColumns });

  // Columnas para el selector: usar las columnas de AG Grid convertidas a TanStack
  const selectorColumns = useMemo(() => tanStackColumns, [tanStackColumns]);

  const renderOrders = () => {
    if (isMobile) {
      return <CardList {...ordersViewProps} showActions={true} />;
    }

    // 🎯 Usar AG Grid para la vista de escritorio
    return (
      <OrdersAgGridTable
        {...ordersViewProps}
        selectedOrders={selectedOrders}
        cancelableCount={actionCounters.cancelableCount}
        preparableCount={actionCounters.preparableCount}
        sendableCount={actionCounters.sendableCount}
        uncancelableCount={actionCounters.uncancelableCount}
        onCancelSelected={handleCancelSelected}
        onPrepareSelected={handlePrepareSelected}
        onMarkForSendSelected={handleMarkForSendSelected}
        onUncancelSelected={handleUncancelSelected}
        onClearSelection={handleClearSelection}
        selectedColumns={selectedColumns}
        fetchNextPage={fetchNextPage}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        onSelectionChanged={handleSelectionChanged}
      />
    );
  };

  // 🎯 Muestra el spinner SOLO en la carga inicial
  if (isInitialLoad) {
    return <LoadingState message={t('loading.message', 'Cargando pedidos...')} />;
  }

  if (error) {
    return <ErrorState error={error} onRetry={refetch} />;
  }

  return (
    <div className="px-6 pt-4">
      {/* Header con filtros y botón crear */}
      <div className="mb-4">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <div className="flex items-center gap-3 flex-wrap">
            <FiltersSection
              handleSearch={handleSearch}
              onFilterChange={handleFilterChange}
              columns={selectorColumns}
              selectedColumns={selectedColumns}
              onColumnsChange={handleColumnsChange}
              filterConditions={filterConditions}
              search={search}
              statusId={statusId}
              sorting={sorting ? { field: sorting.field, direction: sorting.direction || 'asc' } : undefined}
              hasTeamData={hasTeamData}
              searchTerm={searchTerm}
            />
          </div>
          {/* Botón crear - Solo visible si la API devuelve > 0 pedidos */}
          {total > 0 && (
            <div className="shrink-0 absolute right-4 top-2">
              <CreateOrderButton />
            </div>
          )}
          <TotalResults total={total} />
        </div>
        {/* 🎯 Pestañas siempre visibles, incluso cuando no hay pedidos */}
        {!isLoading && (
          <StatusToolbar
            onStatusChange={setStatusId}
            selectedCount={selectedOrders.size}
            onClearSelection={handleClearSelection}
            onCancelSelected={handleCancelSelected}
            onPrepareSelected={handlePrepareSelected}
            onMarkForSendSelected={handleMarkForSendSelected}
            onUncancelSelected={handleUncancelSelected}
            cancelableCount={actionCounters.cancelableCount}
            preparableCount={actionCounters.preparableCount}
            sendableCount={actionCounters.sendableCount}
            uncancelableCount={actionCounters.uncancelableCount}
            statusCounts={statusCounts}
            isLoadingCounts={isLoadingCounts}
            active={activeTab}
            setActive={setActiveTab}
          />
        )}
      </div>

      {/* Estado vacío */}
      {!isInitialLoad && orders.length === 0 && (
        <EmptyView
          type={hasActiveFilters ? 'noResults' : 'emptyState'}
          icon={EMPTY_VIEW_ICONS.ORDERS}
          customAction={hasActiveFilters ? null : <CreateOrderButton inline={true} />}
        />
      )}

      {/* Tabla visible solo si hay resultados */}
      {!isInitialLoad && total > 0 && (
        <div
          className={`transition-opacity duration-300 mb-4 mt-4 ${isFetching && !isFetchingNextPage ? 'opacity-50 pointer-events-none' : 'opacity-100'}`}
        >
          {renderOrders()}
        </div>
      )}
    </div>
  );
};
